import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiBuildHouseAlt } from "@miplatform/assets";
import {
  MiActions,
  MiBox,
  MiButton,
  MiDetailFields,
  MiMenu,
  MiModalConfirm,
  MiPlaceholderArea,
  MiSectionHeader,
  MiTab,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  INPUT_TYPES,
  MODAL_TITLES,
} from "@miplatform/constants";
import { IAdjustmentResponse, IPlanAreaResponse } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiPencil, mdiPlus, mdiTrashCan, mdiTrashCanOutline } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { VerticalTabsSelector } from "../../../../../components/VerticalTabsLayout/components/VerticalTabsSelector";
import { BidDocumentViewer } from "../BidDocumentViewer/BidDocumentViewer";
import { useCreateBidContext } from "../CreateBidContext";
import {
  ADJUSTMENT_TYPE,
  ADJUSTMENT_TYPE_OPTIONS,
  ADJUSTMENT_TYPE_TO_ENUM,
  BID_STATUS,
  DEFAULT_VALUES,
  PRICING_TABS,
  TAB_VALUES,
} from "../constants";
import { pricingAdjustmentSchema } from "../schema/schema";
import { PriceAdjustmentDetail } from "./PriceAdjustmentDetail";
import {
  AdjustmentRequestPayload,
  usePricingAdjustmentsViewModel,
} from "./usePricingAdjustmentsViewModel";

const TYPE_MAP: Record<string, string> = {
  GlobalBid: ADJUSTMENT_TYPE.GLOBAL_BID,
  GlobalDrop: ADJUSTMENT_TYPE.GLOBAL_DROP,
  IndividualDrop: ADJUSTMENT_TYPE.INDIVIDUAL_DROP,
  GlobalItem: ADJUSTMENT_TYPE.GLOBAL_ITEM,
  IndividualItem: ADJUSTMENT_TYPE.INDIVIDUAL_ITEM,
};

const getAdjustmentDisplayInfo = (
  adj: IAdjustmentResponse,
  planAreas: IPlanAreaResponse[]
) => {
  const typeDisplay = TYPE_MAP[String(adj.type)];

  let areaName = adj.planAreaName;
  let itemName = adj.planMaterialName;
  let { categoryName, dropTypeName } = adj;

  if (adj.planAreaId && !areaName) {
    const area = planAreas.find((a) => a.planAreaId === adj.planAreaId);
    if (area) {
      areaName = area.planAreaLabel;
    }
  }

  if (
    adj.planAreaId ||
    adj.planMaterialId ||
    adj.dropTypeId ||
    adj.categoryId
  ) {
    const area = planAreas.find((a) => a.planAreaId === adj.planAreaId);
    const materials = area?.materials ?? [];

    if (adj.planMaterialId && !itemName) {
      const mat = materials.find(
        (m) => (m.planMaterialId || m.materialId) === adj.planMaterialId
      );
      if (mat) {
        itemName =
          [mat.manufacturerPartNumber, mat.itemDescription]
            .filter(Boolean)
            .join(" | ") || mat.materialId;
      }
    }

    if (!categoryName && adj.categoryId) {
      const allDrops = materials.flatMap((m) => m.drops ?? []);
      const drop = allDrops.find((d) => d.categoryId === adj.categoryId);
      if (drop) {
        categoryName = drop.categoryName;
      }
    }

    if (!dropTypeName && adj.dropTypeId) {
      const allDrops = materials.flatMap((m) => m.drops ?? []);
      const drop = allDrops.find((d) => d.dropTypeId === adj.dropTypeId);
      if (drop) {
        dropTypeName = drop.dropTypeName;
      }
    }
  }

  return {
    typeDisplay,
    areaName,
    itemName,
    categoryName,
    dropTypeName,
  };
};

const getReadOnlyFields = (
  adj: IAdjustmentResponse,
  planAreas: IPlanAreaResponse[]
) => {
  const { typeDisplay, areaName, itemName, categoryName, dropTypeName } =
    getAdjustmentDisplayInfo(adj, planAreas);

  return [
    { label: "Label", value: adj.label, width: "35%" },
    { label: "Type", value: typeDisplay, width: "30%" },
    {
      label: "Amt",
      value: adj.amount,
      width: "25%",
      sx: { "& input": { textAlign: "right" } },
      textfieldProps: {
        startAdornment: <Typography>$</Typography>,
      },
    },
    { label: "Area", value: areaName, width: "35%" },
    { label: "Item", value: itemName, width: "58%" },
    { label: "Category", value: categoryName, width: "30%" },
    { label: "Drop", value: dropTypeName, width: "30%" },
    { label: "Notes", value: adj.note, width: "100%" },
  ]
    .filter(({ value }) => value)
    .map((field) => ({
      ...field,
      name: field.label,
      readOnly: true,
      backgroundColor: "transparent",
    }));
};

export function PricingAdjustments() {
  const [currentTab, setCurrentTab] = useState(TAB_VALUES.BID);
  const [openAddAdjustment, setOpenAddAdjustment] = useState(false);
  const [editingAdjustmentId, setEditingAdjustmentId] = useState<string | null>(
    null
  );
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedAdjustmentToDelete, setSelectedAdjustmentToDelete] =
    useState<IAdjustmentResponse | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);

  const { classes, theme } = useStyles();
  const { openDocumentViewer, form: bidForm } = useCreateBidContext();
  const { watch: bidWatch } = bidForm;
  const {
    id: planId,
    builderId,
    options = [],
    planAreas: bidPlanAreas = [],
    planStatus,
  } = bidWatch();
  const form = useForm<CreatePricingAdjustmentForm>({
    mode: "onChange",
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(pricingAdjustmentSchema),
  });
  const {
    formState: { isValid, isDirty },
    control,
    reset,
    getValues,
    setValue,
    trigger,
    watch,
  } = form;
  const { adjustments } = watch();
  const {
    isLoading,
    getPricingDetails,
    createPricingAdjustments,
    updatePricingAdjustments,
    deletePricingAdjustments,
    pricingDetails,
    pricingAdjustments,
  } = usePricingAdjustmentsViewModel({
    planId,
    accountId: builderId,
    reset,
    setOpenAddAdjustment,
  });

  const planDetails = pricingDetails?.planDetails ?? (pricingDetails as any);
  const adjustmentsList =
    pricingDetails?.adjustments ?? (pricingAdjustments as any) ?? [];
  const bidApproved = planStatus === BID_STATUS.APPROVED;

  const maxNegativeAmount = useMemo(() => {
    const planAreas = planDetails?.planAreas ?? [];
    const selectedArea = planAreas.find(
      (area: any) => area.planAreaId === adjustments.area
    );

    if (adjustments.type === ADJUSTMENT_TYPE.GLOBAL_BID) {
      return planDetails?.total;
    }

    if (adjustments.type === ADJUSTMENT_TYPE.GLOBAL_ITEM) {
      return planDetails?.materialsSummary?.total;
    }

    if (adjustments.type === ADJUSTMENT_TYPE.GLOBAL_DROP) {
      return planDetails?.dropsSummary?.total;
    }

    if (adjustments.type === ADJUSTMENT_TYPE.INDIVIDUAL_ITEM) {
      if (!adjustments.item) {
        return undefined;
      }

      return selectedArea?.materials.find(
        (material: any) =>
          (material.planMaterialId || material.materialId) === adjustments.item
      )?.total;
    }

    if (adjustments.type === ADJUSTMENT_TYPE.INDIVIDUAL_DROP) {
      if (!adjustments.category || !adjustments.drop) {
        return undefined;
      }

      return selectedArea?.materials
        .flatMap((material: any) => material.drops ?? [])
        .find(
          (drop: any) =>
            drop.categoryId === adjustments.category &&
            drop.dropTypeId === adjustments.drop
        )?.total;
    }

    return undefined;
  }, [
    adjustments.area,
    adjustments.category,
    adjustments.drop,
    adjustments.item,
    adjustments.type,
    planDetails,
  ]);

  const optionTabs = useMemo(
    () =>
      [...options]
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: "base",
          })
        )
        .map((option) => ({
          label: option.name,
          value: option.id,
        })),
    [options]
  );

  useEffect(() => {
    if (planId && builderId) {
      getPricingDetails();
    }
  }, [planId, builderId]);

  useEffect(() => {
    if (currentTab === TAB_VALUES.OPTIONS) {
      setSelectedOptionId(optionTabs[0]?.value);
    }
    reset(DEFAULT_VALUES);
    setOpenAddAdjustment(false);
    setEditingAdjustmentId(null);
  }, [currentTab]);

  // trigger amount validation to not exceed max total
  useEffect(() => {
    setValue("adjustments.maxNegativeAmount", maxNegativeAmount, {
      shouldValidate: true,
    });
    trigger("adjustments.amt");
  }, [maxNegativeAmount]);

  const handleSave = () => {
    const { adjustments: adj } = getValues();

    const payload: AdjustmentRequestPayload = {
      label: adj.label,
      amount: Number(adj.amt),
      note: adj.notes || null,
      type: ADJUSTMENT_TYPE_TO_ENUM[adj.type] ?? 0,
      planId,
      optionId: null,
      requirementId: null,
      planMaterialId: null,
      planAreaId: null,
      categoryId: null,
      floorId: null,
      dropTypeId: null,
    };

    if (adj.type === ADJUSTMENT_TYPE.INDIVIDUAL_ITEM) {
      payload.planMaterialId = adj.item || null;
      payload.planAreaId = adj.area || null;
      payload.categoryId = null;
      payload.floorId = null;
      payload.dropTypeId = null;
    }

    if (adj.type === ADJUSTMENT_TYPE.INDIVIDUAL_DROP) {
      payload.planMaterialId = null;
      payload.categoryId = adj.category || null;
      payload.dropTypeId = adj.drop || null;
      payload.planAreaId = adj.area || null;

      const planAreas = planDetails?.planAreas ?? [];
      const selectedArea = planAreas.find(
        (area: any) => area.planAreaId === adj.area
      );
      const areaDrops = (selectedArea?.materials ?? []).flatMap(
        (material: any) => material.drops ?? []
      );
      const selectedDrop = areaDrops.find(
        (drop: any) =>
          drop.categoryId === adj.category && drop.dropTypeId === adj.drop
      );
      const floorId =
        selectedDrop?.floorId ||
        bidPlanAreas.find((a: any) => a.id === adj.area)?.floorId;
      if (floorId) {
        payload.floorId = floorId;
      }
    }

    createPricingAdjustments(payload);
  };

  const handleStartEdit = (adj: IAdjustmentResponse) => {
    setOpenAddAdjustment(false);
    setEditingAdjustmentId(adj.id);

    const mappedType = TYPE_MAP[String(adj.type)];

    reset({
      adjustments: {
        label: adj.label,
        type: mappedType,
        amt: adj.amount,
        notes: adj.note || "",
        area: adj.planAreaId || "",
        item: adj.planMaterialId || "",
        category: adj.categoryId || "",
        drop: adj.dropTypeId || "",
      },
    });
  };

  const handleCancelEdit = () => {
    setEditingAdjustmentId(null);
    reset(DEFAULT_VALUES);
  };

  const handleSaveEdit = (adjustment: IAdjustmentResponse) => {
    const { adjustments: adj } = getValues();

    const payload: AdjustmentRequestPayload = {
      label: adj.label,
      amount: Number(adj.amt),
      note: adj.notes || null,
      type: ADJUSTMENT_TYPE_TO_ENUM[adj.type] ?? 0,
      planId,
      optionId: null,
      requirementId: null,
      planMaterialId: adj.item || null,
      planAreaId: adj.area || null,
      categoryId: adj.category || null,
      floorId: null,
      dropTypeId: adj.drop || null,
      modifiedOn: adjustment.modifiedOn,
    };

    updatePricingAdjustments(payload, adjustment.id);
    setEditingAdjustmentId(null);
    reset(DEFAULT_VALUES);
  };

  const detailFields = useMemo(() => {
    const isEditing = Boolean(editingAdjustmentId);
    const fields = [];
    const planAreas = planDetails?.planAreas ?? [];
    const selectedArea = planAreas.find(
      (area: any) => area.planAreaId === adjustments.area
    );
    const materials = selectedArea?.materials ?? [];
    const areaDrops = materials.flatMap(
      (material: any) => material.drops ?? []
    );
    const areaOptions = toSelectOptions(
      planAreas.map((area: any) => ({
        label: area.planAreaLabel,
        value: area.planAreaId,
      }))
    );
    const dropAreaOptions = toSelectOptions(
      planAreas
        .filter((area: any) =>
          (area.materials ?? []).some(
            (material: any) => (material.drops ?? []).length > 0
          )
        )
        .map((area: any) => ({
          label: area.planAreaLabel,
          value: area.planAreaId,
        }))
    );
    const itemOptions = toSelectOptions(
      materials.map((material: any) => ({
        label:
          [material.manufacturerPartNumber, material.itemDescription]
            .filter(Boolean)
            .join(" | ") || material.materialId,
        value: material.planMaterialId || material.materialId,
      }))
    );
    const categoryOptions = toSelectOptions(
      areaDrops.map((drop: any) => ({
        label: drop.categoryName,
        value: drop.categoryId,
      }))
    );
    const dropOptions = toSelectOptions(
      areaDrops
        .filter((drop: any) => drop.categoryId === adjustments.category)
        .map((drop: any) => ({
          label: drop.dropTypeName,
          value: drop.dropTypeId,
        }))
    );

    const handleAreaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(`adjustments.area`, e.target.value, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(`adjustments.item`, "", { shouldValidate: true });
      setValue(`adjustments.category`, "", { shouldValidate: true });
      setValue(`adjustments.drop`, "", { shouldValidate: true });
    };

    fields.push(
      {
        label: "Label",
        name: `adjustments.label`,
        required: true,
      },
      {
        label: "Type",
        name: `adjustments.type`,
        fieldType: INPUT_TYPES.SELECT,
        required: true,
        disabled: isEditing,
        options: ADJUSTMENT_TYPE_OPTIONS,
        setValue: (e: React.ChangeEvent<HTMLInputElement>) => {
          const type = e.target.value;

          setValue(`adjustments.type`, type, {
            shouldValidate: true,
            shouldDirty: true,
          });
          setValue(`adjustments.area`, "");

          if (type !== ADJUSTMENT_TYPE.INDIVIDUAL_ITEM) {
            setValue(`adjustments.item`, "");
          }
          if (type !== ADJUSTMENT_TYPE.INDIVIDUAL_DROP) {
            setValue(`adjustments.category`, "");
            setValue(`adjustments.drop`, "");
          }
        },
      }
    );

    fields.push({
      label: "Amt",
      name: `adjustments.amt`,
      required: true,
      fieldType: INPUT_TYPES.NUMERIC,
      value: adjustments.amt ?? "",
      sx: { "& input": { textAlign: "right" } },
      decimalScale: 2,
      allowNegative: true,
      InputProps: {
        startAdornment: <Typography>$</Typography>,
      },
      setValue: (e: number) => {
        setValue(`adjustments.amt`, e, {
          shouldValidate: true,
          shouldDirty: true,
        });
      },
    });

    if (adjustments.type === ADJUSTMENT_TYPE.INDIVIDUAL_ITEM) {
      fields.push(
        {
          label: "Area",
          name: `adjustments.area`,
          fieldType: INPUT_TYPES.SELECT,
          width: "50%",
          required: true,
          disabled: isEditing,
          options: areaOptions,
          setValue: handleAreaChange,
        },
        {
          label: "Item",
          name: `adjustments.item`,
          fieldType: INPUT_TYPES.SELECT,
          width: "50%",
          required: true,
          options: itemOptions,
          disabled: isEditing || !adjustments.area,
        }
      );
    }
    if (adjustments.type === ADJUSTMENT_TYPE.INDIVIDUAL_DROP) {
      fields.push(
        {
          label: "Area",
          name: `adjustments.area`,
          fieldType: INPUT_TYPES.SELECT,
          required: true,
          disabled: isEditing,
          options: dropAreaOptions,
          setValue: handleAreaChange,
        },
        {
          label: "Category",
          name: `adjustments.category`,
          fieldType: INPUT_TYPES.SELECT,
          required: true,
          options: categoryOptions,
          disabled: isEditing || !adjustments.area,
          setValue: (e: React.ChangeEvent<HTMLInputElement>) => {
            setValue(`adjustments.category`, e.target.value, {
              shouldValidate: true,
              shouldDirty: true,
            });
            setValue(`adjustments.drop`, "", { shouldValidate: true });
          },
        },
        {
          label: "Drop",
          name: `adjustments.drop`,
          fieldType: INPUT_TYPES.SELECT,
          required: true,
          options: dropOptions,
          disabled: isEditing || !adjustments.area || !adjustments.category,
        }
      );
    }

    fields.push({
      label: "Notes",
      name: `adjustments.notes`,
    });

    return fields;
  }, [
    adjustments.type,
    adjustments.area,
    adjustments.category,
    adjustments.amt,
    planDetails,
    editingAdjustmentId,
  ]);

  const renderAdjustmentsList = () => {
    const planAreas = planDetails?.planAreas ?? [];

    return (
      <Box className={classes.adjustmentsListContainer}>
        {adjustmentsList.map((adj: IAdjustmentResponse) => {
          if (editingAdjustmentId === adj.id) {
            return (
              <Box key={adj.id} className={classes.editItemContainer}>
                <MiDetailFields control={control} detailFields={detailFields} />
                <Box className={classes.adjustmentActionsBox}>
                  <MiActions
                    actions={[
                      {
                        title: BUTTON_LABELS.CANCEL,
                        variant: BUTTON_VARIANTS.OUTLINED,
                        className: classes.actionButton,
                        onClick: handleCancelEdit,
                      },
                      {
                        title: BUTTON_LABELS.SAVE,
                        variant: BUTTON_VARIANTS.CONTAINED,
                        className: classes.actionButton,
                        disabled: !isValid || !isDirty || isLoading,
                        onClick: () => handleSaveEdit(adj),
                      },
                    ]}
                  />
                </Box>
              </Box>
            );
          }

          return (
            <Box key={adj.id} className={classes.adjustmentCard}>
              <Box className={classes.readOnlyFieldsBox}>
                <MiDetailFields
                  detailFields={getReadOnlyFields(adj, planAreas)}
                />
              </Box>
              <Box className={classes.cardColMenu}>
                <MiMenu
                  disabled={
                    openAddAdjustment ||
                    Boolean(editingAdjustmentId) ||
                    bidApproved
                  }
                  data={[
                    {
                      label: BUTTON_LABELS.EDIT,
                      icon: mdiPencil,
                      action: () => handleStartEdit(adj),
                    },
                    {
                      label: BUTTON_LABELS.DELETE,
                      icon: mdiTrashCanOutline,
                      color: theme.palette.red.main,
                      action: () => {
                        setSelectedAdjustmentToDelete(adj);
                        setOpenDeleteModal(true);
                      },
                    },
                  ]}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderAdjustmentsForm = () => (
    <Box className={classes.adjustmentsPanel}>
      <MiSectionHeader
        title="Bid Adjustments"
        variant="text"
        rightAction={
          <MiButton
            title="Add Adjustment"
            variant="contained"
            startIcon={{ path: mdiPlus }}
            disabled={
              openAddAdjustment || Boolean(editingAdjustmentId) || bidApproved
            }
            onClick={() => {
              setEditingAdjustmentId(null);
              reset(DEFAULT_VALUES);
              setOpenAddAdjustment(true);
            }}
          />
        }
      />
      {openAddAdjustment && (
        <Box className={classes.addItemContainer}>
          <MiDetailFields control={control} detailFields={detailFields} />
          <Box className={classes.adjustmentActionsBox}>
            <MiActions
              actions={[
                {
                  title: BUTTON_LABELS.CANCEL,
                  variant: BUTTON_VARIANTS.OUTLINED,
                  className: classes.actionButton,
                  onClick: () => {
                    setOpenAddAdjustment(false);
                    reset(DEFAULT_VALUES);
                  },
                },
                {
                  title: BUTTON_LABELS.SAVE,
                  variant: BUTTON_VARIANTS.CONTAINED,
                  className: classes.actionButton,
                  disabled: !isValid || !isDirty || isLoading,
                  onClick: handleSave,
                },
              ]}
            />
          </Box>
        </Box>
      )}
      {adjustmentsList.length > 0 && renderAdjustmentsList()}
      {!openAddAdjustment && adjustmentsList.length === 0 && (
        <MiPlaceholderArea
          text="No Adjustments Created"
          helperText="Tip: To create, click the + Add Adjustment button"
          bgColor={theme.palette.primary.light}
          containerStyle={{ margin: theme.spacing(1) }}
          iconComponent={<img src={MiBuildHouseAlt} alt="House Icon" />}
        />
      )}
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={openDeleteModal}
        textPositive={BUTTON_LABELS.DELETE}
        buttonPositiveColor={BUTTON_COLORS.RED}
        titleIcon={{ path: mdiTrashCan, color: theme.palette.red.main }}
        handleNegative={() => {
          setOpenDeleteModal(false);
          setSelectedAdjustmentToDelete(null);
        }}
        handlePositive={() => {
          if (selectedAdjustmentToDelete) {
            deletePricingAdjustments(selectedAdjustmentToDelete);
          }
          setOpenDeleteModal(false);
          setSelectedAdjustmentToDelete(null);
        }}
        description={
          <Typography>
            {`Are you sure you want to delete ${
              selectedAdjustmentToDelete?.label
                ? `"${selectedAdjustmentToDelete.label}"`
                : "this adjustment"
            }?`}
          </Typography>
        }
      />
    </Box>
  );

  return (
    <Box className={classes.root}>
      <MiBox
        headerTitle="Pricing Adjustments"
        className={classes.miBox}
        height="auto"
      >
        <MiTab
          tabs={PRICING_TABS}
          currentTab={currentTab}
          onTabChange={setCurrentTab}
          borderBottom
        />
        <Box className={classes.contentBox}>
          {currentTab === TAB_VALUES.OPTIONS && (
            <Box className={classes.optionTabsBox}>
              <VerticalTabsSelector
                tabs={optionTabs}
                selectedTab={selectedOptionId}
                onTabSelect={(item) => {
                  if (item) {
                    setSelectedOptionId(item.value as string);
                  }
                }}
                width={160}
              />
            </Box>
          )}
          <Box className={classes.leftContentBox}>
            {openDocumentViewer && renderAdjustmentsForm()}
            <Box className={classes.priceDetailsBox}>
              <PriceAdjustmentDetail
                planDetails={pricingDetails?.planDetails}
              />
            </Box>
          </Box>
          {!openDocumentViewer && renderAdjustmentsForm()}
        </Box>
      </MiBox>
      <BidDocumentViewer />
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    overflow: "hidden",
    height: "100%",
  },
  miBox: {
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  contentBox: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 0),
  },
  leftContentBox: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    flex: 1,
    overflow: "auto",
  },
  optionTabsBox: {
    backgroundColor: theme.palette.grey.light,
  },
  adjustmentsPanel: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.grey.light,
    borderRadius: 1,
    padding: theme.spacing(1),
    overflow: "auto",
  },
  priceDetailsBox: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
  },
  adjustmentActionsBox: {
    display: "flex",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    width: "40%",
  },
  actionButton: {
    width: "100%",
  },
  addItemContainer: {
    marginBottom: theme.spacing(2),
  },
  editItemContainer: {
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.grey.accent}`,
  },
  adjustmentsListContainer: {
    display: "flex",
    flexDirection: "column",
  },
  adjustmentCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(0.75),
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.grey.accent}`,
  },
  readOnlyFieldsBox: {
    flex: 1,
    minWidth: 0,
  },
  cardColMenu: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingTop: theme.spacing(1),
  },
}));

// filter duplicate options
function toSelectOptions(options: SelectOption[]) {
  const seen = new Set<string>();

  return options.filter((option) => {
    if (!option.value || seen.has(option.value)) {
      return false;
    }
    seen.add(option.value);
    return true;
  });
}

interface SelectOption {
  label: string;
  value: string;
}

interface CreatePricingAdjustmentForm {
  adjustments: {
    label: string;
    type: string;
    amt: string | number;
    notes: string;
    item?: string;
    area?: string;
    category?: string;
    drop?: string;
    maxNegativeAmount?: number;
  };
}
